import { useEnterLecturePageMode, useExitLecturePageMode, useLecturePageFromParams, useLecturePageLink, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { useCreateLesson, useDeleteLecture, useDuplicateLecture, useHandleReorder, useHideAddUIOnOpen } from 'lecture_pages/hooks/lecture-page-edit-top-panel';
import React, { useContext, useCallback, useMemo, useState, useEffect } from 'react';
import t from 'react-translate';

import { AngularServicesContext } from 'react-app';

// redux
import { useSelector } from 'react-redux';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { unsetCurrentCourseCourseLongTeamSet, unsetCurrentCourseGroupTeamSet } from 'redux/actions/lecture-components';
import { duplicateLecturePage, deleteLecturePage, createLecturePage } from 'redux/actions/lecture-pages';
import { getFullCourseTimelineWithProgress } from 'redux/actions/timeline';
import { RootState } from 'redux/schemas';
import { CourseAliases } from 'redux/schemas/models/course';
import { LecturePage, SyncingStatus } from 'redux/schemas/models/lecture-page';
import { getCourseAliases, getCurrentCourse, getCurrentCourseCourseLongTeamSet, getCurrentCourseGroupTeamSet, getFlatCourseAliases } from 'redux/selectors/course';
import { CannotBeCopiedComponentsMeta, getCannotBeCopiedComponentsMeta, getNearestLecture } from 'redux/selectors/lecture-page';
import { useAppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { createLecturePage as createCollectionLecturePage, getCanLinkCollectionLesson, getCanConvertToCollectionLesson, canConvertToCollectionLecture } from 'redux/actions/collections';
import { useCourseOutline } from 'redux/selectors/course-outline/use-course-outline';

// components
import NvDropdown, { NvDropdownOption, NvDropdownButtonStyle, NvDropdownAlign } from 'shared/components/inputs/nv-dropdown';
import { isEmpty } from 'underscore';
import { hideAddUI } from 'lecture_pages/templates/components/nv-add-component';
import LinkCollectionLessonsModal from 'courses/components/course-lesson-link/link-lessons-modal';
import AddToCollectionModal from 'courses/components/course-lesson-to-collection/add-to-collection-modal';
import { LecturePageMode } from '..';

export const LectureContentAdminDropdown = () => {
  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const lectureLink = useLecturePageLink();
  const enterMode = useEnterLecturePageMode();
  const exitMode = useExitLecturePageMode();
  const handleReorder = useHandleReorder();
  const duplicateThisLecture = useDuplicateLecture();
  const createLesson = useCreateLesson();
  const hideAddUIOnOpen = useHideAddUIOnOpen();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { $state, CurrentPermissionsManager, CurrentCourseManager } = useContext(AngularServicesContext);

  const lecturePage = useLecturePageFromParams();
  const currentUserId = useSelector(state => state.app.currentUserId);
  const currentInstitutionId = useSelector(state => state.app.currentInstitutionId);
  const currentCourse = useSelector(getCurrentCourse);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const cannotBeCopiedComponentsMeta: CannotBeCopiedComponentsMeta[] = useSelector((state: RootState) => getCannotBeCopiedComponentsMeta(state, state.app.lecturePage.currentLectureId));
  const aliases: CourseAliases = useSelector((state: RootState) => getCourseAliases(state));
  const currentCourseLongTeamSet = useSelector(state => getCurrentCourseCourseLongTeamSet(state));
  const currentCourseGroupTeamSet = useSelector(state => getCurrentCourseGroupTeamSet(state));
  const { canLinkCollectionLesson, canConvertToCollectionLesson, canLessonContentConvertibleToCollection } = useSelector(state => state.app.collection);

  const courseOutline = useCourseOutline(params.catalogId);
  const deleteThisLecture = useDeleteLecture();

  const lectureName = lecturePage.title;

  const adminDropdownItems = useMemo<NvDropdownOption[]>(() => {
    const items: NvDropdownOption[] = [];

    if (params.mode === LecturePageMode.EDIT || params.mode === LecturePageMode.LINKED_EDIT) {
      items.push({
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.EXIT_EDIT_MODE(),
        callback: () => exitMode(),
        dataQa: 'exit-edit-mode-option',
      });
    } else if (params.mode === LecturePageMode.RESTRICTED_EDIT || params.mode === LecturePageMode.LINKED_EDIT) {
      items.push({
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.EXIT_ADMIN_MODE(),
        callback: () => exitMode(),
        dataQa: 'exit-admin-mode-option',
      });
    } else {
      items.push({
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.EDIT(),
        callback: () => enterMode(LecturePageMode.EDIT),
        dataQa: 'edit-lesson-option',
        disabled: SyncingStatus.SYNC_IN_PROGRESS === lecturePage.syncingStatus,
      });
    }

    if (CurrentPermissionsManager.isCourseBuilder() || CurrentPermissionsManager.hasCollectionBuilderPermissions()) {
      items.push(
        /** Preserve the previous mode entered in the nav logic if we entered from an edit mode. This way we will
         * return to the edit mode when exiting the reorder mode */
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.REORDER(),
          disabled: (lecturePage.isLinked || SyncingStatus.SYNC_IN_PROGRESS === lecturePage.syncingStatus),
          callback: handleReorder,
          dataQa: 'reorder-lesson-option',
        },
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.DUPLICATE(courseAliases),
          callback: duplicateThisLecture,
          dataQa: 'duplicate-lesson-option',
        },
      );

      const disableDelete = currentCourse?.isContentManagementCollection
        && (lecturePage.contentManagementLinks?.length > 0
          || lecturePage.syncingStatus === SyncingStatus.SYNC_IN_PROGRESS);

      items.push(
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.DELETE(),
          class: 'text-danger',
          callback: deleteThisLecture,
          dataQa: 'delete-lesson-option',
          disabled: disableDelete,
          tooltip: {
            text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.DELETE_TOOLTIP(),
            enabled: disableDelete,
          },
        },
        { type: 'divider' },
        {
          type: 'text',
          text: currentCourse?.isContentManagementCollection
            ? t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.LECTURE_LIST.LESSON_ACTION.NEW_LESSON()
            : t.LECTURE_PAGES.ADMIN.COG_OPTIONS.CREATE(courseAliases),
          callback: createLesson,
          dataQa: 'create-lesson-option',
        },
      );
    }

    return items;
  }, [
    courseAliases, createLesson, currentCourse?.isContentManagementCollection,
    CurrentPermissionsManager, deleteThisLecture, duplicateThisLecture,
    enterMode, exitMode, lecturePage.contentManagementLinks?.length,
    lecturePage.isLinked, params.mode, lecturePage.syncingStatus,
    handleReorder,
  ]);

  const handleToggle = useCallback(
    (showList) => {
      setIsDropdownOpen(showList);
      if (showList) {
        hideAddUIOnOpen();
      }
    },
    [hideAddUIOnOpen],
  );

  return (
    <React.Fragment>
      <NvDropdown
        buttonStyle={NvDropdownButtonStyle.ICON}
        iconClass='icon-smallest icon-settings mr-4'
        items={adminDropdownItems}
        align={NvDropdownAlign.RIGHT}
        offset={10}
        minWidth={220}
        onToggle={handleToggle}
        ariaExpanded={isDropdownOpen}
        altLabel='gear icon'
        toggleDataQa='lecture-page-content-settings'
        ariaRole='menu'
      />
    </React.Fragment>
  );
};

export default LectureContentAdminDropdown;
